import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { DatePicker } from 'antd';
import { RangeDatePicker as StyledRangeDatePicker } from './styles';

const { RangePicker } = DatePicker;

type BaseProps = React.ComponentProps<typeof RangePicker>;

type Props = Omit<BaseProps, 'picker'>;

const ModalRangePicker: React.FC<Props> = ({
  getPopupContainer,
  dropdownAlign,
  onOpenChange,
  ...rest
}) => {
  const triggerRef = useRef<HTMLSpanElement | null>(null);

  const getContainer: NonNullable<BaseProps['getPopupContainer']> = (triggerNode) => {
    const node = triggerNode as HTMLElement | null;
    return (
      (node?.closest('.ant-modal-body') as HTMLElement) ||
      (node?.closest('.ant-modal') as HTMLElement) ||
      (node?.closest('.ant-drawer-body') as HTMLElement) ||
      (node?.closest('.ant-drawer') as HTMLElement) ||
      (node?.parentElement as HTMLElement)
    );
  };

  const getContainerMemo = useCallback<NonNullable<BaseProps['getPopupContainer']>>(
    (node) => (getPopupContainer ? getPopupContainer(node) : getContainer(node)),
    [getPopupContainer]
  );

  const computeDx = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return 0;
    const container = getContainerMemo(el);
    if (!container) return 0;
    const triggerRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const containerCenterX = containerRect.left + containerRect.width / 2;
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    return Math.round(containerCenterX - triggerCenterX);
  }, [getContainerMemo]);

  const [open, setOpen] = useState<boolean | undefined>(undefined);
  const [dx, setDx] = useState(0);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (nextOpen) {
        requestAnimationFrame(() => setDx(computeDx()));
      }
      onOpenChange?.(nextOpen);
    },
    [computeDx, onOpenChange]
  );

  useEffect(() => {
    if (!open) return;
    const onResize = () => setDx(computeDx());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, computeDx]);

  const align = useMemo(() => {
    return (
      dropdownAlign || {
        points: ['tc', 'bc'],
        offset: [dx, 8],
        overflow: { adjustX: true, adjustY: true },
      }
    );
  }, [dropdownAlign, dx]);

  const scale = isMobile ? Math.max(0.5, Math.min(1.2, 0.6)) : 0.8;

  return (
    <span ref={triggerRef} style={{ display: 'inline-block' }}>
      <StyledRangeDatePicker
        getPopupContainer={getContainerMemo}
        dropdownAlign={align as any}
        dropdownClassName="modal-range-picker-dropdown"
        onOpenChange={handleOpenChange}
        panelRender={(panel) => (
          <div
            style={{
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
            }}
          >
            {panel}
          </div>
        )}
        {...rest}
      />
    </span>
  );
};

export default ModalRangePicker;
